import React, { useState, useEffect, useCallback, useRef } from 'react';
import { t } from '../../translation';
import { GalleryPhoto, filterDamagesByPhoto } from '../../transform-cameras';
import { buildAuthHeaders } from '../../helpers';
import { clampPan as clampPanUtil, normalizeWheelDelta, ZOOM_MIN, ZOOM_MAX } from '../wheel-zoom-utils';

/* ── Interfaces ─────────────────────────────────────────────────────────── */

export interface FallbackGalleryProps {
    isOpen: boolean;
    photos: GalleryPhoto[];
    onClose: () => void;
    requestId: string;
    apiBaseUrl: string;
    apiStagePath: string;
    apiPrefix: string;
}

interface FallbackMarker {
    id: string;
    centerX: number;
    centerY: number;
    width: number;
    height: number;
}

interface FallbackNote {
    id: string;
    dateTime: string;
    author: string;
    message: string;
}

interface FallbackDamageRecord {
    id: string;
    name: string;
    description: string;
    selectValue: string;
    photoId: string;
    cameraId: string;
    markers: FallbackMarker[];
    notes: FallbackNote[];
}

type DamageMode = 'view' | 'select' | 'new' | 'confirmDelete' | 'notes';
type NoteEditorMode = 'none' | 'new' | 'edit';

/* ── Constants ──────────────────────────────────────────────────────────── */

const GRID_COLUMNS = 4;
const DEFAULT_MARKER_SIZE = 40;

/* ── Component ──────────────────────────────────────────────────────────── */

const FallbackGallery: React.FC<FallbackGalleryProps> = ({
    isOpen,
    photos,
    onClose,
    requestId,
    apiBaseUrl,
    apiStagePath,
    apiPrefix
}) => {
    /* ── Gallery state ─────────────────────────────────────────────────── */
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [errorImages, setErrorImages] = useState<Set<string>>(new Set());
    const overlayRef = useRef<HTMLDivElement>(null);
    const expandedImgRef = useRef<HTMLImageElement>(null);

    /* ── Damage records ────────────────────────────────────────────────── */
    const [damages, setDamages] = useState<FallbackDamageRecord[]>([]);
    const [selectedDamageId, setSelectedDamageId] = useState('');
    const [damageCarouselIndex, setDamageCarouselIndex] = useState(0);
    const [damageMode, setDamageMode] = useState<DamageMode>('view');
    const [damageForm, setDamageForm] = useState({ name: '', description: '', selectValue: '' });
    const [damageMenuVisible, setDamageMenuVisible] = useState(false);

    /* ── Marker state (inspector) ──────────────────────────────────────── */
    const [inspectorOpen, setInspectorOpen] = useState(false);
    const [createMode, setCreateMode] = useState(false);
    const [markers, setMarkers] = useState<FallbackMarker[]>([]);
    const [draftMarker, setDraftMarker] = useState<FallbackMarker | null>(null);
    const [selectedMarkerId, setSelectedMarkerId] = useState('');
    const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
    const [pendingMarkerForNewDamage, setPendingMarkerForNewDamage] = useState<FallbackMarker | null>(null);
    const inspectorViewportRef = useRef<HTMLDivElement>(null);
    const inspectorImageRef = useRef<HTMLImageElement>(null);
    const drawStartRef = useRef<{ x: number; y: number } | null>(null);
    const resizeStartRef = useRef<{ startX: number; startY: number; marker: FallbackMarker } | null>(null);

    /* ── Notes state ───────────────────────────────────────────────────── */
    const [noteEditorMode, setNoteEditorMode] = useState<NoteEditorMode>('none');
    const [selectedNoteId, setSelectedNoteId] = useState('');
    const [noteDeleteConfirmOpen, setNoteDeleteConfirmOpen] = useState(false);
    const [noteForm, setNoteForm] = useState({ dateTime: '', author: '', message: '' });

    /* ── No-model popup (shown once) ───────────────────────────────────── */
    const [noModelPopupVisible, setNoModelPopupVisible] = useState(true);

    /* ── Inspector zoom & pan state ────────────────────────────────────── */
    const [inspectorZoom, setInspectorZoom] = useState(1);
    const [inspectorPan, setInspectorPan] = useState({ x: 0, y: 0 });
    const inspectorPanDragRef = useRef<{ startX: number; startY: number; startPan: { x: number; y: number } } | null>(null);

    /* ── Derived ───────────────────────────────────────────────────────── */
    const currentPhoto = expandedIndex !== null ? photos[expandedIndex] : null;
    const photoDamages = currentPhoto ? filterDamagesByPhoto(damages as any, currentPhoto.id) as unknown as FallbackDamageRecord[] : [];
    const currentDamage = damages.find(d => d.id === selectedDamageId) || null;
    const currentNotes = currentDamage?.notes || [];

    /* ── Effects ────────────────────────────────────────────────────────── */

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            setExpandedIndex(null);
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    // Load existing damages from API when gallery opens
    useEffect(() => {
        if (!isOpen || !requestId) return;
        const base = `${apiBaseUrl}/${apiStagePath}/${apiPrefix}/damages`;
        const loadUrl = `${base}/${requestId}`;

        (async () => {
            try {
                const res = await fetch(loadUrl, { method: 'GET', mode: 'cors', headers: buildAuthHeaders() });
                if (!res.ok) {
                    if (res.status === 404) console.info('[FallbackGallery] No existing damages found for this request');
                    return;
                }
                const data = await res.json();
                const damageList = Array.isArray(data?.damageList) ? data.damageList : [];
                const loaded: FallbackDamageRecord[] = [];
                for (const d of damageList) {
                    if (d?.markerMetadata === true || d?.name === '__camera_markers__') continue;
                    if (d?.source !== 'fallback-gallery') continue;
                    loaded.push({
                        id: d.id || `fb-loaded-${Date.now()}-${Math.random()}`,
                        name: d.name || '',
                        description: d.description || '',
                        selectValue: d.selectValue || d.location || '',
                        photoId: Array.isArray(d.photoIds) && d.photoIds.length > 0 ? `photo-${d.photoIds[0]}` : (d.cameraId || ''),
                        cameraId: d.cameraId || (Array.isArray(d.cameraIds) ? d.cameraIds[0] : '') || '',
                        markers: Array.isArray(d.imageMarkers) ? d.imageMarkers.map((m: any) => ({
                            id: m.id || `mk-${Date.now()}-${Math.random()}`,
                            centerX: m.centerX || 0,
                            centerY: m.centerY || 0,
                            width: m.width || 40,
                            height: m.height || 40
                        })) : [],
                        notes: Array.isArray(d.notes) ? d.notes.map((n: any) => ({
                            id: n.id || `nt-${Date.now()}-${Math.random()}`,
                            dateTime: n.dateTime || '',
                            author: n.author || '',
                            message: n.message || ''
                        })) : []
                    });
                }
                if (loaded.length > 0) {
                    setDamages(loaded);
                    console.info(`[FallbackGallery] Loaded ${loaded.length} damages from API`);
                }
            } catch (err) {
                console.warn('[FallbackGallery] Failed to load damages from API:', err);
            }
        })();
    }, [isOpen, requestId, apiBaseUrl, apiStagePath, apiPrefix]);

    // Reset damage panel when changing photos — auto-select first damage if any
    useEffect(() => {
        setDamageMode('view');
        setInspectorOpen(false);
        setCreateMode(false);
        setDraftMarker(null);
        setSelectedMarkerId('');
        setPendingMarkerForNewDamage(null);
        setNoteEditorMode('none');
        setSelectedNoteId('');
        setNoteDeleteConfirmOpen(false);

        // Auto-select first damage for this photo
        if (expandedIndex !== null && photos[expandedIndex]) {
            const photoId = photos[expandedIndex].id;
            const photoDmgs = damages.filter((d: any) => d.photoId === photoId);
            if (photoDmgs.length > 0) {
                setSelectedDamageId(photoDmgs[0].id);
                setDamageCarouselIndex(0);
                setDamageMenuVisible(true);
            } else {
                setSelectedDamageId('');
                setDamageCarouselIndex(0);
                setDamageMenuVisible(false);
            }
        } else {
            setSelectedDamageId('');
            setDamageCarouselIndex(0);
            setDamageMenuVisible(false);
        }
    }, [expandedIndex]);

    // Sync markers when selected damage changes
    useEffect(() => {
        if (currentDamage) {
            setMarkers(currentDamage.markers || []);
        } else {
            setMarkers([]);
        }
    }, [selectedDamageId, damages]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;
            if (e.key === 'Escape') {
                if (inspectorOpen) { setInspectorOpen(false); return; }
                if (expandedIndex !== null) { setExpandedIndex(null); return; }
                onClose();
                return;
            }
            if (expandedIndex === null || inspectorOpen) return;
            if (e.key === 'ArrowLeft') setExpandedIndex(prev => prev !== null && prev > 0 ? prev - 1 : photos.length - 1);
            if (e.key === 'ArrowRight') setExpandedIndex(prev => prev !== null && prev < photos.length - 1 ? prev + 1 : 0);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, expandedIndex, photos.length, onClose, inspectorOpen]);

    /* ── Save to API ──────────────────────────────────────────────────── */

    const apiBase = `${apiBaseUrl}/${apiStagePath}/${apiPrefix}/damages`;

    const sendDamagesToApi = async (allDamages: FallbackDamageRecord[]) => {
        console.log('[FallbackGallery] sendDamagesToApi called with', allDamages.length, 'damages');
        console.log('[FallbackGallery] API URL:', apiBase, 'requestId:', requestId);

        if (!requestId) {
            console.error('[FallbackGallery] No requestId — cannot save to API');
            return;
        }

        const damageList = allDamages.map(d => {
            // Extract numeric frameNum from photoId (format: "photo-N")
            const numericPhotoId = parseInt(String(d.photoId).replace(/^photo-/, ''), 10);
            const photoIds = Number.isFinite(numericPhotoId) ? [numericPhotoId] : [];
            return {
                id: d.id,
                name: d.name,
                description: d.description,
                selectValue: d.selectValue,
                photoIds,
                position: { x: '0', y: '0', z: '0' },
                cameraId: d.cameraId,
                cameraIds: [d.cameraId],
                notes: (d.notes || []).map(n => ({ id: n.id, dateTime: n.dateTime, author: n.author, message: n.message })),
                imageMarkers: (d.markers || []).map(m => ({ id: m.id, centerX: m.centerX, centerY: m.centerY, width: m.width, height: m.height })),
                source: 'fallback-gallery'
            };
        });

        try {
            // Step 1: Check if damages already exist
            let exists = false;
            const getUrl = `${apiBase}/${requestId}`;
            console.log('[FallbackGallery] Step 1: GET', getUrl);
            try {
                const r = await fetch(getUrl, { method: 'GET', mode: 'cors', headers: buildAuthHeaders() });
                console.log('[FallbackGallery] GET response:', r.status);
                if (r.ok) exists = true;
            } catch (getErr) {
                console.warn('[FallbackGallery] GET check failed:', getErr);
            }

            // Step 2: POST or PUT
            const method = exists ? 'PUT' : 'POST';
            const url = exists ? `${apiBase}/${requestId}` : apiBase;
            const bodyObj = exists ? { damageList } : { requestId, damageList };
            console.log('[FallbackGallery] Step 2:', method, url);
            console.log('[FallbackGallery] Payload:', JSON.stringify(bodyObj).substring(0, 200));

            const res = await fetch(url, {
                method,
                mode: 'cors',
                headers: buildAuthHeaders(),
                body: JSON.stringify(bodyObj)
            });

            console.log('[FallbackGallery] Response:', res.status, res.statusText);
            if (!res.ok) {
                const errText = await res.text();
                console.error('[FallbackGallery] API error body:', errText);
                throw new Error(`API ${res.status}: ${errText}`);
            }
            console.info('[FallbackGallery] ✓ Damages saved to API successfully');
        } catch (err) {
            console.error('[FallbackGallery] API save failed:', err);
        }
    };

    /* ── Damage CRUD helpers ────────────────────────────────────────────── */

    const generateId = () => `fb-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

    const saveDamageRecord = () => {
        if (!currentPhoto) return;
        const { name, description, selectValue } = damageForm;
        if (!name.trim() || !description.trim() || !selectValue.trim()) {
            alert(t('markers.fillAllFields'));
            return;
        }

        const existing = damages.find(d => d.id === selectedDamageId);
        let next: FallbackDamageRecord[];
        if (existing) {
            next = damages.map(d => d.id === selectedDamageId
                ? { ...d, name: name.trim(), description: description.trim(), selectValue: selectValue.trim() }
                : d
            );
        } else {
            const initialMarkers = pendingMarkerForNewDamage ? [pendingMarkerForNewDamage] : [];
            const newDamage: FallbackDamageRecord = {
                id: generateId(),
                name: name.trim(),
                description: description.trim(),
                selectValue: selectValue.trim(),
                photoId: currentPhoto.id,
                cameraId: currentPhoto.cameraId,
                markers: initialMarkers,
                notes: []
            };
            next = [...damages, newDamage];
            setTimeout(() => {
                setSelectedDamageId(newDamage.id);
                setDamageMenuVisible(true);
            }, 0);
        }

        setDamages(next);
        setPendingMarkerForNewDamage(null);
        setDamageMode('view');
        console.log('[FallbackGallery] saveDamageRecord: calling sendDamagesToApi with', next.length, 'damages');
        sendDamagesToApi(next);
    };

    const confirmDeleteDamage = () => {
        const updated = damages.filter(d => d.id !== selectedDamageId);
        setDamages(updated);
        setSelectedDamageId('');
        setDamageMode('view');
        setDamageCarouselIndex(0);
        sendDamagesToApi(updated);
    };

    const setDamageCarouselSafe = useCallback((idx: number) => {
        if (photoDamages.length === 0) return;
        const clamped = ((idx % photoDamages.length) + photoDamages.length) % photoDamages.length;
        setDamageCarouselIndex(clamped);
        const d = photoDamages[clamped];
        if (d) {
            setSelectedDamageId(d.id);
            setDamageForm({ name: d.name, description: d.description, selectValue: d.selectValue });
        }
    }, [photoDamages]);

    /* ── Notes CRUD helpers ─────────────────────────────────────────────── */

    const buildDefaultNoteDateTime = () => {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    };

    const startNewNote = useCallback(() => {
        setNoteEditorMode('new');
        setSelectedNoteId('');
        setNoteDeleteConfirmOpen(false);
        setNoteForm({ dateTime: buildDefaultNoteDateTime(), author: '', message: '' });
    }, []);

    const startEditNote = useCallback(() => {
        const note = currentNotes.find(n => n.id === selectedNoteId);
        if (!note) return;
        setNoteEditorMode('edit');
        setNoteDeleteConfirmOpen(false);
        const dt = note.dateTime ? note.dateTime.slice(0, 16) : '';
        setNoteForm({ dateTime: dt, author: note.author, message: note.message });
    }, [currentNotes, selectedNoteId]);

    const saveNote = useCallback(() => {
        if (!noteForm.author.trim() || !noteForm.message.trim()) {
            alert(t('markers.fillAllFields'));
            return;
        }
        setDamages(prev => prev.map(d => {
            if (d.id !== selectedDamageId) return d;
            let notes = [...(d.notes || [])];
            if (noteEditorMode === 'edit' && selectedNoteId) {
                notes = notes.map(n => n.id === selectedNoteId
                    ? { ...n, dateTime: noteForm.dateTime || new Date().toISOString(), author: noteForm.author.trim(), message: noteForm.message.trim() }
                    : n
                );
            } else {
                notes.push({
                    id: `note-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
                    dateTime: noteForm.dateTime || new Date().toISOString(),
                    author: noteForm.author.trim(),
                    message: noteForm.message.trim()
                });
            }
            return { ...d, notes };
        }));
        setNoteEditorMode('none');
        setNoteForm({ dateTime: '', author: '', message: '' });
    }, [noteForm, noteEditorMode, selectedNoteId, selectedDamageId]);

    const confirmDeleteNote = useCallback(() => {
        setDamages(prev => prev.map(d => {
            if (d.id !== selectedDamageId) return d;
            return { ...d, notes: (d.notes || []).filter(n => n.id !== selectedNoteId) };
        }));
        setSelectedNoteId('');
        setNoteDeleteConfirmOpen(false);
    }, [selectedDamageId, selectedNoteId]);

    /* ── Marker helpers (inspector) ─────────────────────────────────────── */

    const persistMarkers = useCallback((next: FallbackMarker[]) => {
        setMarkers(next);
        if (selectedDamageId) {
            setDamages(prev => {
                const updated = prev.map(d => d.id === selectedDamageId ? { ...d, markers: next } : d);
                sendDamagesToApi(updated);
                return updated;
            });
        }
    }, [selectedDamageId]);

    // Confirm a draft marker: if no damage exists, store as pending and open damage form
    const confirmDraftMarker = useCallback(() => {
        if (!draftMarker) return;
        const confirmed: FallbackMarker = { ...draftMarker, id: draftMarker.id === 'draft' ? generateId() : draftMarker.id };
        if (!selectedDamageId) {
            // No damage yet — store marker as pending and prompt damage creation
            setPendingMarkerForNewDamage(confirmed);
            setDraftMarker(null);
            setInspectorOpen(false);
            setDamageMenuVisible(true);
            setDamageMode('new');
            setDamageForm({ name: '', description: '', selectValue: '' });
            return;
        }
        // Damage exists — add marker directly
        const next = [...markers, confirmed];
        persistMarkers(next);
        setDraftMarker(null);
        setCreateMode(true);
    }, [draftMarker, selectedDamageId, markers, persistMarkers]);

    const onInspectorImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    }, []);

    const getImageRenderMetrics = useCallback(() => {
        const viewport = inspectorViewportRef.current;
        if (!viewport || naturalSize.w === 0 || naturalSize.h === 0) return null;
        const containerRect = viewport.getBoundingClientRect();
        const cW = containerRect.width;
        const cH = containerRect.height;
        const fitScale = Math.min(cW / naturalSize.w, cH / naturalSize.h);
        // Apply zoom to rendered dimensions
        const rW = naturalSize.w * fitScale * inspectorZoom;
        const rH = naturalSize.h * fitScale * inspectorZoom;
        // Image center with pan offset
        const centerX = (cW * 0.5) + inspectorPan.x;
        const centerY = (cH * 0.5) + inspectorPan.y;
        const oX = centerX - rW * 0.5;
        const oY = centerY - rH * 0.5;
        return { rW, rH, oX, oY, containerRect };
    }, [naturalSize, inspectorZoom, inspectorPan]);

    const onInspectorViewportMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!createMode) return;
        if ((e.target as HTMLElement).closest('.camera-photo-inspector-zoom-controls')) return;
        const metrics = getImageRenderMetrics();
        if (!metrics) return;
        const x = e.clientX - metrics.containerRect.left - metrics.oX;
        const y = e.clientY - metrics.containerRect.top - metrics.oY;
        if (x < 0 || y < 0 || x > metrics.rW || y > metrics.rH) return;
        drawStartRef.current = { x, y };
    }, [createMode, getImageRenderMetrics]);

    const onInspectorViewportMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        // Handle resize drag
        if (resizeStartRef.current) {
            const metrics = getImageRenderMetrics();
            if (!metrics) return;
            const curX = e.clientX - metrics.containerRect.left - metrics.oX;
            const curY = e.clientY - metrics.containerRect.top - metrics.oY;
            const scaleX = naturalSize.w / metrics.rW;
            const scaleY = naturalSize.h / metrics.rH;
            const orig = resizeStartRef.current.marker;
            const left = orig.centerX - orig.width / 2;
            const top = orig.centerY - orig.height / 2;
            const newRight = curX * scaleX;
            const newBottom = curY * scaleY;
            const newW = Math.max(10, newRight - left);
            const newH = Math.max(10, newBottom - top);
            setDraftMarker({
                ...orig,
                centerX: Math.round(left + newW / 2),
                centerY: Math.round(top + newH / 2),
                width: Math.round(newW),
                height: Math.round(newH)
            });
            return;
        }
        // Handle draw drag
        if (!drawStartRef.current) return;
        const metrics = getImageRenderMetrics();
        if (!metrics) return;
        const curX = e.clientX - metrics.containerRect.left - metrics.oX;
        const curY = e.clientY - metrics.containerRect.top - metrics.oY;
        const scaleX = naturalSize.w / metrics.rW;
        const scaleY = naturalSize.h / metrics.rH;
        const sx = drawStartRef.current.x * scaleX;
        const sy = drawStartRef.current.y * scaleY;
        const cx = curX * scaleX;
        const cy = curY * scaleY;
        const w = Math.abs(cx - sx);
        const h = Math.abs(cy - sy);
        if (w < 5 && h < 5) return;
        setDraftMarker({
            id: 'draft',
            centerX: Math.round((sx + cx) / 2),
            centerY: Math.round((sy + cy) / 2),
            width: Math.round(w),
            height: Math.round(h)
        });
    }, [naturalSize, getImageRenderMetrics]);

    const onInspectorViewportMouseUp = useCallback(() => {
        if (resizeStartRef.current) {
            resizeStartRef.current = null;
            return;
        }
        if (!drawStartRef.current) return;
        drawStartRef.current = null;
        if (draftMarker && (draftMarker.width <= 5 || draftMarker.height <= 5)) {
            setDraftMarker(null);
        }
    }, [draftMarker]);

    const onDraftResizeMouseDown = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!draftMarker) return;
        resizeStartRef.current = { startX: e.clientX, startY: e.clientY, marker: { ...draftMarker } };
    }, [draftMarker]);

    const onInspectorViewportClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!createMode) return;
        if (draftMarker) return; // draft already exists, user should confirm it
        if ((e.target as HTMLElement).closest('.camera-photo-inspector-zoom-controls')) return;
        const metrics = getImageRenderMetrics();
        if (!metrics) return;
        const x = e.clientX - metrics.containerRect.left - metrics.oX;
        const y = e.clientY - metrics.containerRect.top - metrics.oY;
        if (x < 0 || y < 0 || x > metrics.rW || y > metrics.rH) return;
        const scaleX = naturalSize.w / metrics.rW;
        const scaleY = naturalSize.h / metrics.rH;
        // Create as draft — user must confirm
        setDraftMarker({
            id: 'draft',
            centerX: Math.round(x * scaleX),
            centerY: Math.round(y * scaleY),
            width: DEFAULT_MARKER_SIZE,
            height: DEFAULT_MARKER_SIZE
        });
        setCreateMode(false);
    }, [createMode, draftMarker, naturalSize, getImageRenderMetrics]);

    const deleteSelectedMarker = useCallback(() => {
        const next = markers.filter(m => m.id !== selectedMarkerId);
        persistMarkers(next);
        setSelectedMarkerId('');
    }, [markers, selectedMarkerId, persistMarkers]);

    const getInspectorDisplayMetrics = useCallback(() => {
        const viewport = inspectorViewportRef.current;
        if (!viewport || naturalSize.w === 0 || naturalSize.h === 0) return null;
        const viewW = viewport.clientWidth;
        const viewH = viewport.clientHeight;
        const fitScale = Math.min(viewW / naturalSize.w, viewH / naturalSize.h);
        const baseW = naturalSize.w * fitScale;
        const baseH = naturalSize.h * fitScale;
        const displayW = baseW * inspectorZoom;
        const displayH = baseH * inspectorZoom;
        const centerX = (viewW * 0.5) + inspectorPan.x;
        const centerY = (viewH * 0.5) + inspectorPan.y;
        return { viewW, viewH, displayW, displayH, centerX, centerY, naturalW: naturalSize.w, naturalH: naturalSize.h };
    }, [naturalSize, inspectorZoom, inspectorPan]);

    const getInspectorMarkerStyle = useCallback((marker: FallbackMarker) => {
        const metrics = getInspectorDisplayMetrics();
        if (!metrics) return { display: 'none' as const };

        const left = metrics.centerX - (metrics.displayW * 0.5)
            + ((marker.centerX - marker.width / 2) / metrics.naturalW) * metrics.displayW;
        const top = metrics.centerY - (metrics.displayH * 0.5)
            + ((marker.centerY - marker.height / 2) / metrics.naturalH) * metrics.displayH;
        const width = (marker.width / metrics.naturalW) * metrics.displayW;
        const height = (marker.height / metrics.naturalH) * metrics.displayH;

        return {
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`
        };
    }, [getInspectorDisplayMetrics]);

    const getNormalMarkerStyle = useCallback((marker: FallbackMarker) => {
        if (!expandedImgRef.current || naturalSize.w === 0) return { display: 'none' as const };
        const img = expandedImgRef.current;
        const containerRect = img.parentElement?.getBoundingClientRect();
        if (!containerRect) return { display: 'none' as const };

        const containerW = containerRect.width;
        const containerH = containerRect.height;
        const imgAspect = naturalSize.w / naturalSize.h;
        const containerAspect = containerW / containerH;
        let renderedW: number, renderedH: number, offsetX: number, offsetY: number;
        if (imgAspect > containerAspect) {
            renderedW = containerW;
            renderedH = containerW / imgAspect;
            offsetX = 0;
            offsetY = (containerH - renderedH) / 2;
        } else {
            renderedH = containerH;
            renderedW = containerH * imgAspect;
            offsetX = (containerW - renderedW) / 2;
            offsetY = 0;
        }

        const scaleX = renderedW / naturalSize.w;
        const scaleY = renderedH / naturalSize.h;
        return {
            left: `${offsetX + (marker.centerX - marker.width / 2) * scaleX}px`,
            top: `${offsetY + (marker.centerY - marker.height / 2) * scaleY}px`,
            width: `${marker.width * scaleX}px`,
            height: `${marker.height * scaleY}px`
        };
    }, [naturalSize]);

    const onNormalImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    }, []);

    /* ── Inspector zoom & pan helpers ──────────────────────────────────── */

    // Reset zoom when inspector opens/closes or photo changes
    useEffect(() => {
        setInspectorZoom(1);
        setInspectorPan({ x: 0, y: 0 });
    }, [inspectorOpen, expandedIndex]);

    const clampInspectorPan = useCallback((pan: { x: number; y: number }, zoom: number) => {
        const viewport = inspectorViewportRef.current;
        if (!viewport || naturalSize.w === 0 || naturalSize.h === 0) return pan;
        return clampPanUtil(pan, zoom, viewport.clientWidth, viewport.clientHeight, naturalSize.w, naturalSize.h);
    }, [naturalSize]);

    const onInspectorWheel = useCallback((e: WheelEvent) => {
        if (!inspectorOpen) return;
        e.preventDefault();
        e.stopPropagation();
        const delta = normalizeWheelDelta(e.deltaY, e.deltaMode);
        setInspectorZoom(prev => {
            const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, prev + delta));
            setInspectorPan(prevPan => {
                if (next <= 1) return { x: 0, y: 0 };
                const viewport = inspectorViewportRef.current;
                if (!viewport || naturalSize.w === 0) return prevPan;
                return clampPanUtil(prevPan, next, viewport.clientWidth, viewport.clientHeight, naturalSize.w, naturalSize.h);
            });
            return next;
        });
    }, [inspectorOpen, naturalSize]);

    // Attach wheel listener to inspector viewport (needs passive: false)
    useEffect(() => {
        const viewport = inspectorViewportRef.current;
        if (!viewport || !inspectorOpen) return;
        viewport.addEventListener('wheel', onInspectorWheel, { passive: false });
        return () => viewport.removeEventListener('wheel', onInspectorWheel);
    }, [onInspectorWheel, inspectorOpen]);

    const onInspectorZoomSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const zoom = Number(e.target.value);
        setInspectorZoom(zoom);
        setInspectorPan(prev => {
            if (zoom <= 1) return { x: 0, y: 0 };
            return clampInspectorPan(prev, zoom);
        });
    }, [clampInspectorPan]);

    const onInspectorPanMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (inspectorZoom <= 1 || e.button !== 0) return;
        // Don't start pan if clicking on a button or marker
        if (createMode) return;
        const target = e.target as HTMLElement;
        if (target.closest('button') || target.closest('.camera-photo-marker') || target.closest('.camera-photo-marker-contextual-buttons')) return;
        inspectorPanDragRef.current = { startX: e.clientX, startY: e.clientY, startPan: { ...inspectorPan } };
    }, [inspectorZoom, inspectorPan, createMode]);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!inspectorPanDragRef.current) return;
            const dx = e.clientX - inspectorPanDragRef.current.startX;
            const dy = e.clientY - inspectorPanDragRef.current.startY;
            const nextPan = {
                x: inspectorPanDragRef.current.startPan.x + dx,
                y: inspectorPanDragRef.current.startPan.y + dy
            };
            setInspectorPan(clampInspectorPan(nextPan, inspectorZoom));
        };
        const onMouseUp = () => { inspectorPanDragRef.current = null; };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [inspectorZoom, clampInspectorPan]);

    /* ── Early return ──────────────────────────────────────────────────── */

    if (!isOpen) return null;
    const isEmpty = photos.length === 0;
    const damageCount = photoDamages.length;
    const damageCarouselLabel = damageCount > 0
        ? `${damageCarouselIndex + 1} / ${damageCount}`
        : `0 / 0`;

    /* ── Render ─────────────────────────────────────────────────────────── */

    return (
        <>
            {/* ── Grid overlay ──────────────────────────────────────────── */}
            <div className="fallback-gallery-overlay" ref={overlayRef} onWheel={e => e.stopPropagation()}>
                <div className="fallback-gallery-container" onWheel={e => e.stopPropagation()}>
                    <div className="fallback-gallery-header">
                        <h2 className="fallback-gallery-title">{t('fallbackGallery.title')}</h2>
                        <div className="fallback-gallery-header-actions">
                        </div>
                    </div>
                    <div className="fallback-gallery-content">
                        {isEmpty && <div className="fallback-gallery-empty">{t('fallbackGallery.noPhotos')}</div>}
                        {!isEmpty && (
                            <div className="fallback-gallery-grid" style={{ gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)` }}>
                                {photos.map((photo, index) => {
                                    const count = filterDamagesByPhoto(damages as any, photo.id).length;
                                    return (
                                        <div key={photo.id} className="fallback-gallery-cell" onClick={() => setExpandedIndex(index)} role="button" tabIndex={0}
                                            aria-label={photo.name || `${t('fallbackGallery.photo')} ${index + 1}`}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedIndex(index); } }}>
                                            {errorImages.has(photo.id)
                                                ? <div className="fallback-gallery-cell-error">⚠</div>
                                                : <img src={photo.url} alt={photo.name || `${t('fallbackGallery.photo')} ${index + 1}`} className="fallback-gallery-cell-img"
                                                    onError={() => setErrorImages(prev => new Set(prev).add(photo.id))} />}
                                            {count > 0 && <div className="fallback-gallery-cell-badge">{count}</div>}
                                            <div className="fallback-gallery-cell-label">{photo.name || `${t('fallbackGallery.photo')} ${index + 1}`}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── No-model popup (shown once on open) ───────────────────── */}
                {noModelPopupVisible && (
                    <div className="fallback-gallery-no-model-popup-overlay">
                        <div className="fallback-gallery-no-model-popup">
                            <p className="fallback-gallery-no-model-popup-text">{t('fallbackGallery.noModelPopup')}</p>
                            <div className="fallback-gallery-no-model-popup-actions">
                                <button className="fallback-gallery-no-model-popup-close" onClick={() => setNoModelPopupVisible(false)}>
                                    {t('fallbackGallery.noModelPopupClose')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Expanded photo view (reuses gallery-enlarged-* classes) ── */}
            {expandedIndex !== null && currentPhoto && (
                <div className="gallery-enlarged-overlay" onWheel={e => e.stopPropagation()}>
                    <div className="gallery-enlarged-container" onClick={e => e.stopPropagation()}>
                        <div className="gallery-enlarged-media">
                            <div className="gallery-enlarged-image-frame">
                                <button className="gallery-enlarged-action gallery-enlarged-close" onClick={() => setExpandedIndex(null)} title={t('fallbackGallery.close')}>×</button>
                                <button className="gallery-enlarged-action gallery-enlarged-expand" onClick={() => {
                                    setInspectorOpen(true);
                                    setCreateMode(true);
                                    setDraftMarker(null);
                                    setSelectedMarkerId('');
                                    setNaturalSize({ w: 0, h: 0 });
                                }} title={t('damage.createWithMark')}>+</button>

                                {photos.length > 1 && (
                                    <button className="gallery-enlarged-nav gallery-enlarged-prev"
                                        onClick={() => setExpandedIndex(prev => prev !== null && prev > 0 ? prev - 1 : photos.length - 1)}
                                        title={t('fallbackGallery.previous')}>‹</button>
                                )}

                                <img ref={expandedImgRef} src={currentPhoto.url}
                                    alt={currentPhoto.name || `${t('fallbackGallery.photo')} ${expandedIndex + 1}`}
                                    className="gallery-enlarged-image" onLoad={onNormalImageLoad}
                                    onError={() => setErrorImages(prev => new Set(prev).add(currentPhoto.id))} />

                                {/* Rectangular markers (same CSS as normal mode) */}
                                {markers.map(marker => {
                                    const style = getNormalMarkerStyle(marker);
                                    return (
                                        <div key={marker.id}
                                            className={`camera-photo-marker camera-photo-marker--readonly camera-photo-marker--highlighted`}
                                            style={style} />
                                    );
                                })}

                                {/* Show pending marker during damage creation */}
                                {pendingMarkerForNewDamage && damageMode === 'new' && (
                                    <div className="camera-photo-marker camera-photo-marker--draft"
                                        style={getNormalMarkerStyle(pendingMarkerForNewDamage)} />
                                )}

                                {photos.length > 1 && (
                                    <button className="gallery-enlarged-nav gallery-enlarged-next"
                                        onClick={() => setExpandedIndex(prev => prev !== null && prev < photos.length - 1 ? prev + 1 : 0)}
                                        title={t('fallbackGallery.next')}>›</button>
                                )}
                            </div>
                        </div>

                        {/* Info row */}
                        <div className="gallery-enlarged-info-row">
                            <div className="gallery-enlarged-info">
                                {`${t('fallbackGallery.photo')} ${expandedIndex + 1} ${t('fallbackGallery.of')} ${photos.length}`}
                                {currentPhoto.name && ` — ${currentPhoto.name}`}
                            </div>
                        </div>

                        {/* ── Damage panel — shown when damages exist or creating new ── */}
                        {damageMenuVisible && (damageCount > 0 || damageMode === 'new') && (
                        <div className="camera-damage-panel">
                            <div className="camera-damage-panel-title">{t('damage.sheetTitle')}</div>

                            {/* VIEW mode */}
                            {damageMode === 'view' && (
                                <>
                                    {currentDamage ? (
                                        <>
                                            <div className="camera-damage-row"><strong>{t('markers.tableName')}:</strong> {currentDamage.name}</div>
                                            <div className="camera-damage-row"><strong>{t('markers.tableDescription')}:</strong> {currentDamage.description}</div>
                                            <div className="camera-damage-row"><strong>{t('markers.tableSide')}:</strong> {currentDamage.selectValue}</div>
                                            <div className="camera-damage-actions">
                                                <button className="camera-damage-button" onClick={() => {
                                                    setDamageMode('new');
                                                    setDamageForm({ name: currentDamage.name, description: currentDamage.description, selectValue: currentDamage.selectValue });
                                                }}>{t('markers.edit')}</button>
                                                <button className="camera-damage-button" onClick={() => {
                                                    setDamageMode('notes');
                                                    setNoteEditorMode('none');
                                                    setSelectedNoteId(currentNotes[0]?.id || '');
                                                    setNoteDeleteConfirmOpen(false);
                                                }}>{t('cameraPositions.viewNotes')}</button>
                                                <button className="camera-damage-button" onClick={() => setDamageMode('confirmDelete')}>{t('markers.delete')}</button>
                                            </div>
                                        </>
                                    ) : null}
                                    <div className="camera-damage-actions">
                                        <button className="camera-damage-button" onClick={() => setDamageMenuVisible(false)}>{t('gallery.close')}</button>
                                    </div>
                                    {damageCount > 0 && (
                                        <div className="camera-damage-carousel-row">
                                            <button className="camera-damage-button camera-damage-button--mini" onClick={() => setDamageCarouselSafe(damageCarouselIndex - 1)}>{t('gallery.previous')}</button>
                                            <div className="camera-damage-pagination">{damageCarouselLabel}</div>
                                            <button className="camera-damage-button camera-damage-button--mini" onClick={() => setDamageCarouselSafe(damageCarouselIndex + 1)}>{t('gallery.next')}</button>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* NEW / EDIT mode */}
                            {damageMode === 'new' && (
                                <div className="camera-damage-form">
                                    <input className="camera-damage-input" type="text" value={damageForm.name} placeholder={t('markers.sphereName')}
                                        onChange={e => setDamageForm(prev => ({ ...prev, name: e.target.value }))} />
                                    <textarea className="camera-damage-textarea" value={damageForm.description} placeholder={t('markers.sphereDescription')}
                                        onChange={e => setDamageForm(prev => ({ ...prev, description: e.target.value }))} />
                                    <select className="camera-damage-select" value={damageForm.selectValue}
                                        onChange={e => setDamageForm(prev => ({ ...prev, selectValue: e.target.value }))}>
                                        <option value="">{t('markers.selectSide')}</option>
                                        <option value="Front">{t('markers.sideFront')}</option>
                                        <option value="Left">{t('markers.sideLeft')}</option>
                                        <option value="Right">{t('markers.sideRight')}</option>
                                        <option value="Back">{t('markers.sideBack')}</option>
                                    </select>
                                    <div className="camera-damage-actions">
                                        <button className="camera-damage-button" onClick={saveDamageRecord}>{t('markers.accept')}</button>
                                        <button className="camera-damage-button" onClick={() => {
                                            setPendingMarkerForNewDamage(null);
                                            setDamageMode('view');
                                        }}>{t('markers.cancel')}</button>
                                    </div>
                                </div>
                            )}

                            {/* CONFIRM DELETE mode */}
                            {damageMode === 'confirmDelete' && (
                                <div className="camera-damage-form">
                                    <div className="camera-damage-row">{t('cameraPositions.deleteConfirmMessage')}</div>
                                    <div className="camera-damage-actions">
                                        <button className="camera-damage-button" onClick={confirmDeleteDamage}>{t('markers.delete')}</button>
                                        <button className="camera-damage-button" onClick={() => setDamageMode('view')}>{t('markers.cancel')}</button>
                                    </div>
                                </div>
                            )}

                            {/* NOTES mode */}
                            {damageMode === 'notes' && (
                                <div className="camera-damage-form">
                                    {noteDeleteConfirmOpen ? (
                                        <div className="camera-damage-form">
                                            <div className="camera-damage-row">{t('cameraPositions.deleteConfirmMessage')}</div>
                                            <div className="camera-damage-actions">
                                                <button className="camera-damage-button" onClick={confirmDeleteNote}>{t('markers.delete')}</button>
                                                <button className="camera-damage-button" onClick={() => setNoteDeleteConfirmOpen(false)}>{t('markers.cancel')}</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="camera-damage-notes-list">
                                                {currentNotes.length === 0 ? (
                                                    <div className="camera-damage-row">{t('cameraPositions.noNotes')}</div>
                                                ) : (
                                                    currentNotes.map(note => {
                                                        const isSelected = note.id === selectedNoteId;
                                                        return (
                                                            <button key={note.id}
                                                                className={`camera-damage-note-item${isSelected ? ' camera-damage-note-item--selected' : ''}`}
                                                                onClick={() => setSelectedNoteId(note.id)} type="button">
                                                                <div className="camera-damage-note-item__meta">
                                                                    <strong>{note.dateTime ? new Date(note.dateTime).toLocaleString() : ''}</strong>
                                                                    <span>{note.author}</span>
                                                                </div>
                                                                <div className="camera-damage-note-item__message">{note.message}</div>
                                                            </button>
                                                        );
                                                    })
                                                )}
                                            </div>

                                            {noteEditorMode !== 'none' && (
                                                <div className="camera-damage-note-editor">
                                                    <input className="camera-damage-input" type="datetime-local" value={noteForm.dateTime}
                                                        onChange={e => setNoteForm(prev => ({ ...prev, dateTime: e.target.value }))} />
                                                    <input className="camera-damage-input" type="text" value={noteForm.author} placeholder={t('cameraPositions.noteAuthor')}
                                                        onChange={e => setNoteForm(prev => ({ ...prev, author: e.target.value }))} />
                                                    <textarea className="camera-damage-textarea" value={noteForm.message} placeholder={t('cameraPositions.noteMessage')}
                                                        onChange={e => setNoteForm(prev => ({ ...prev, message: e.target.value }))} />
                                                    <div className="camera-damage-actions">
                                                        <button className="camera-damage-button" onClick={saveNote}>{t('markers.accept')}</button>
                                                        <button className="camera-damage-button" onClick={() => { setNoteEditorMode('none'); setNoteForm({ dateTime: '', author: '', message: '' }); }}>{t('markers.cancel')}</button>
                                                    </div>
                                                </div>
                                            )}

                                            {noteEditorMode === 'none' && (
                                                <div className="camera-damage-actions">
                                                    <button className="camera-damage-button" onClick={startNewNote}>{t('cameraPositions.newNote')}</button>
                                                    <button className="camera-damage-button" onClick={startEditNote} disabled={!selectedNoteId}>{t('cameraPositions.editSelectedNote')}</button>
                                                    <button className="camera-damage-button" onClick={() => setNoteDeleteConfirmOpen(true)} disabled={!selectedNoteId}>{t('cameraPositions.deleteSelectedNote')}</button>
                                                    <button className="camera-damage-button" onClick={() => setDamageMode('view')}>{t('cameraPositions.exitNotes')}</button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Inspector overlay (marker creation) ───────────────────── */}
            {inspectorOpen && currentPhoto && (
                <div className="camera-photo-inspector-overlay" onClick={() => setInspectorOpen(false)}>
                    <div className="camera-photo-inspector-modal" onClick={e => e.stopPropagation()}>
                        <div className="camera-photo-inspector-header">
                            <button
                                className={`camera-photo-inspector-button${createMode ? ' camera-photo-inspector-button--active' : ''}`}
                                onClick={() => { setCreateMode(!createMode); setSelectedMarkerId(''); }}
                                title={t('markers.create')}
                            >
                                <svg className="camera-photo-pin-icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                                    <circle cx="12" cy="9" r="2.8" className="camera-photo-pin-icon__center" />
                                </svg>
                            </button>
                            <button className="camera-photo-inspector-button" onClick={() => setInspectorOpen(false)} title={t('gallery.close')}>×</button>
                        </div>

                        <div className="camera-photo-inspector-viewport" ref={inspectorViewportRef}
                            onClick={onInspectorViewportClick}
                            onMouseDown={(e) => {
                                onInspectorViewportMouseDown(e);
                                onInspectorPanMouseDown(e);
                            }}
                            onMouseMove={onInspectorViewportMouseMove}
                            onMouseUp={onInspectorViewportMouseUp}
                        >
                            <img ref={inspectorImageRef} src={currentPhoto.url} alt={currentPhoto.name || ''}
                                className="camera-photo-inspector-image" onLoad={onInspectorImageLoad}
                                draggable={false} style={{
                                    cursor: createMode ? 'crosshair' : (inspectorZoom > 1 ? 'grab' : 'default'),
                                    transform: `translate(-50%, -50%) translate(${inspectorPan.x}px, ${inspectorPan.y}px) scale(${inspectorZoom})`
                                }} />

                            {/* Existing markers */}
                            {markers.map(marker => {
                                const style = getInspectorMarkerStyle(marker);
                                const isSelected = marker.id === selectedMarkerId;
                                return (
                                    <div key={marker.id}
                                        className={`camera-photo-marker${isSelected ? ' camera-photo-marker--selected' : ''} camera-photo-marker--highlighted`}
                                        style={style}
                                        onClick={e => { e.stopPropagation(); setSelectedMarkerId(marker.id); setCreateMode(false); }}
                                    />
                                );
                            })}

                            {/* Draft marker with contextual confirm/cancel buttons */}
                            {draftMarker && (() => {
                                const draftStyle = getInspectorMarkerStyle(draftMarker);
                                if ('display' in draftStyle) return null;
                                const draftLeft = parseFloat(String(draftStyle.left)) || 0;
                                const draftTop = parseFloat(String(draftStyle.top)) || 0;
                                const draftWidth = parseFloat(String(draftStyle.width)) || 0;
                                const draftHeight = parseFloat(String(draftStyle.height)) || 0;
                                    return (
                                    <>
                                        <div className="camera-photo-marker camera-photo-marker--draft"
                                            style={draftStyle}>
                                            <div className="camera-photo-marker-resize"
                                                onMouseDown={onDraftResizeMouseDown} />
                                        </div>
                                        <div className="camera-photo-marker-contextual-buttons"
                                            style={{
                                                left: `${draftLeft + draftWidth}px`,
                                                top: `${draftTop + draftHeight + 4}px`
                                            }}>
                                            <button
                                                className="camera-photo-marker-contextual-btn camera-photo-marker-contextual-btn--confirm"
                                                onClick={e => { e.stopPropagation(); confirmDraftMarker(); }}
                                                title={t('damage.confirmMark')}
                                            >✓</button>
                                            <button
                                                className="camera-photo-marker-contextual-btn camera-photo-marker-contextual-btn--cancel"
                                                onClick={e => { e.stopPropagation(); setDraftMarker(null); setCreateMode(true); }}
                                                title={t('damage.cancelMark')}
                                            >✕</button>
                                        </div>
                                    </>
                                );
                            })()}
                            <div className="camera-photo-inspector-zoom-controls">
                                <span>{t('position.zoom')}</span>
                                <input
                                    type="range"
                                    min={1}
                                    max={4}
                                    step={0.05}
                                    value={inspectorZoom}
                                    onChange={onInspectorZoomSliderChange}
                                />
                                <span>{`${inspectorZoom.toFixed(2)}x`}</span>
                            </div>
                        </div>

                        {/* Inspector actions — selected marker delete */}
                        {selectedMarkerId && !draftMarker && (
                            <div className="camera-damage-actions" style={{ padding: '8px' }}>
                                <button className="camera-damage-button" onClick={deleteSelectedMarker}>{t('markers.delete')}</button>
                                <button className="camera-damage-button" onClick={() => setSelectedMarkerId('')}>{t('markers.cancel')}</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default FallbackGallery;
